/**
 * Phase 8 T-17 §23.A — Smart Notifications panel.
 *
 * 380 px slide-over from the right, below the 48 px topbar. Sections:
 *   Today / Yesterday / This week / Earlier
 *
 * Only "Today" is expanded by default; the rest collapse after the first.
 *
 * Each row: entity icon · 1-line message · 11 px timestamp · 6 px emerald
 * unread dot. Hover reveals a `[⋯]` context menu with:
 *   Mark as read / Snooze / Open
 *
 * Empty state: inbox icon + "You're all caught up." + "Showing last 30 days".
 *
 * Closes on: Esc key, outside click, route change (caller's responsibility
 * to bind to `useEffect(…, [location.pathname])`).
 *
 * Data source: `useCalendarReminders` (existing). Each `DueCalendarReminder`
 * is treated as an unread notification — marking as read calls `acknowledge`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, ChevronDown, Inbox, MoreHorizontal, Settings, X } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { useCalendarReminders } from '@core/hooks/useCalendarReminders';
import type { DueCalendarReminder } from '@core/supabase/services/calendarEventsService';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function dayBucket(
  isoDate: string,
  now: Date,
): 'today' | 'yesterday' | 'this-week' | 'earlier' {
  const d = new Date(isoDate);
  const diffDays = Math.floor(
    (now.setHours(0, 0, 0, 0) - d.setHours(0, 0, 0, 0)) / 86_400_000,
  );
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return 'this-week';
  return 'earlier';
}

function relativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function reminderMessage(r: DueCalendarReminder): string {
  if (r.offset_minutes === 0) return `Starting now: ${r.event_title}`;
  if (r.offset_minutes < 60)
    return `Upcoming in ${r.offset_minutes} min: ${r.event_title}`;
  return `Upcoming in ${Math.round(r.offset_minutes / 60)}h: ${r.event_title}`;
}

/** ISO string for when the reminder fired (event_starts_at − offset). */
function firedAt(r: DueCalendarReminder): string {
  return new Date(
    new Date(r.event_starts_at).getTime() - r.offset_minutes * 60_000,
  ).toISOString();
}

/* ── Section label map ────────────────────────────────────────────────────── */

const SECTION_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'this-week': 'This week',
  earlier: 'Earlier',
};
const SECTION_ORDER = ['today', 'yesterday', 'this-week', 'earlier'] as const;
type SectionKey = (typeof SECTION_ORDER)[number];

/* ── Main component ───────────────────────────────────────────────────────── */

interface NotificationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsPanel({ open, onOpenChange }: NotificationsPanelProps) {
  const { user } = useAuth();
  const { dueReminders, acknowledge, requestBrowserPermission } =
    useCalendarReminders(user?.orgId, user?.id);

  const unreadCount = dueReminders.length;

  // Track which rows have had their context menu open.
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);

  // Sections that are currently collapsed. "today" starts expanded.
  const [collapsed, setCollapsed] = useState<Set<SectionKey>>(
    new Set(['yesterday', 'this-week', 'earlier']),
  );

  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onOpenChange]);

  // Close row menu on outside click.
  useEffect(() => {
    if (!menuOpenFor) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-notif-menu]')) return;
      setMenuOpenFor(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpenFor]);

  const toggleSection = useCallback((key: SectionKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    for (const r of dueReminders) acknowledge(r.id);
  }, [dueReminders, acknowledge]);

  // Group reminders into buckets.
  const now = useMemo(() => new Date(), []);
  const sections = useMemo(() => {
    const map = new Map<SectionKey, DueCalendarReminder[]>();
    for (const key of SECTION_ORDER) map.set(key, []);
    for (const r of dueReminders) {
      const bucket = dayBucket(firedAt(r), new Date(now));
      map.get(bucket)?.push(r);
    }
    return SECTION_ORDER.filter((k) => (map.get(k)?.length ?? 0) > 0).map(
      (k) => ({ key: k, items: map.get(k)! }),
    );
  }, [dueReminders, now]);

  if (!open) return null;

  return (
    <>
      {/* Click-away backdrop */}
      <div
        className="fixed inset-0 z-[100]"
        aria-hidden
        onClick={() => onOpenChange(false)}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        className="absolute right-0 top-full z-[110] mt-2 flex w-[min(calc(100vw-1rem),380px)] flex-col overflow-hidden rounded-[12px] border shadow-2xl"
        style={{
          background: 'var(--vscode-bg-elevated)',
          borderColor: 'var(--vscode-border-primary)',
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex shrink-0 items-center justify-between border-b px-4 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div className="flex items-center gap-2">
            <h3
              className="text-[14px] font-semibold"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              Notifications
            </h3>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] underline-offset-2 hover:underline"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={requestBrowserPermission}
              className="rounded-[8px] p-1.5 transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              title="Notification settings"
              aria-label="Notification settings"
            >
              <Settings className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-[8px] p-1.5 transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              aria-label="Close notifications"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="max-h-[min(60vh,440px)] overflow-y-auto overscroll-y-contain">
          {/* No org */}
          {!user?.orgId ? (
            <EmptyState text="Join an organisation to see notifications." />
          ) : dueReminders.length === 0 ? (
            <EmptyState />
          ) : (
            sections.map(({ key, items }, sectionIdx) => (
              <section key={key}>
                {/* Section header — always visible; collapsible for non-first sections. */}
                <button
                  type="button"
                  onClick={() => toggleSection(key)}
                  className="flex w-full items-center justify-between border-b px-4 py-2 text-left transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    background:
                      sectionIdx === 0 ? 'transparent' : 'var(--vscode-bg-primary)',
                  }}
                  aria-expanded={!collapsed.has(key)}
                >
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--vscode-text-tertiary)' }}
                  >
                    {SECTION_LABELS[key]}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${
                      collapsed.has(key) ? '-rotate-90' : 'rotate-0'
                    }`}
                    style={{ color: 'var(--vscode-text-tertiary)' }}
                    aria-hidden
                  />
                </button>

                {!collapsed.has(key) ? (
                  <ul>
                    {items.map((r) => (
                      <NotificationRow
                        key={r.id}
                        reminder={r}
                        menuOpen={menuOpenFor === r.id}
                        onMenuToggle={() =>
                          setMenuOpenFor((prev) => (prev === r.id ? null : r.id))
                        }
                        onMarkRead={() => {
                          acknowledge(r.id);
                          setMenuOpenFor(null);
                        }}
                        onSnooze={() => {
                          // Snooze is a future capability — acknowledge for now.
                          acknowledge(r.id);
                          setMenuOpenFor(null);
                        }}
                        onOpen={() => {
                          acknowledge(r.id);
                          setMenuOpenFor(null);
                          onOpenChange(false);
                        }}
                      />
                    ))}
                  </ul>
                ) : null}
              </section>
            ))
          )}
        </div>
      </div>
    </>
  );
}

/* ── Empty state ──────────────────────────────────────────────────────────── */

function EmptyState({ text }: { text?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{
          background: 'var(--vscode-bg-hover)',
          color: 'var(--vscode-text-tertiary)',
        }}
        aria-hidden
      >
        <Inbox className="h-5 w-5" />
      </div>
      <p
        className="text-[13px] font-medium"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        {text ?? "You're all caught up."}
      </p>
      {!text ? (
        <p
          className="text-[11px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Showing last 30 days
        </p>
      ) : null}
    </div>
  );
}

/* ── Single notification row ─────────────────────────────────────────────── */

interface NotificationRowProps {
  reminder: DueCalendarReminder;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMarkRead: () => void;
  onSnooze: () => void;
  onOpen: () => void;
}

function NotificationRow({
  reminder,
  menuOpen,
  onMenuToggle,
  onMarkRead,
  onSnooze,
  onOpen,
}: NotificationRowProps) {
  const fired = firedAt(reminder);
  const message = reminderMessage(reminder);
  const time = relativeTime(fired);

  return (
    <li
      className="group relative flex items-start gap-3 border-b px-4 py-3 last:border-0"
      style={{
        borderColor: 'color-mix(in srgb, var(--vscode-border-primary) 40%, transparent)',
        background: 'color-mix(in srgb, #10B981 4%, transparent)',
      }}
    >
      {/* Entity icon */}
      <div
        className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
        style={{
          background: 'var(--ggj-accent-soft,#D1FAE5)',
          color: '#065F46',
        }}
        aria-hidden
      >
        <Bell className="h-3.5 w-3.5" />
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <p
          className="text-[13px] leading-snug"
          style={{ color: 'var(--vscode-text-primary)' }}
        >
          {message}
        </p>
        <p
          className="mt-1 text-[11px] tabular-nums"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {time}
        </p>
      </div>

      {/* Unread emerald dot — §23.A: 6 px, always shown (all due reminders are unread) */}
      <span
        className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full"
        style={{ background: 'var(--ggj-accent,#10B981)' }}
        aria-label="Unread"
      />

      {/* Context menu trigger — revealed on hover */}
      <div className="relative" data-notif-menu>
        <button
          type="button"
          data-notif-menu
          onClick={(e) => {
            e.stopPropagation();
            onMenuToggle();
          }}
          className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
          style={{ color: 'var(--vscode-text-tertiary)' }}
          aria-label={`Actions for ${message}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
        </button>

        {menuOpen ? (
          <div
            data-notif-menu
            role="menu"
            className="absolute right-0 top-6 z-[120] w-[160px] overflow-hidden rounded-[10px] border py-1 shadow-lg"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
            }}
          >
            {([
              { label: 'Mark as read', action: onMarkRead },
              { label: 'Snooze', action: onSnooze },
              { label: 'Open', action: onOpen },
            ] as const).map(({ label, action }) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                onClick={action}
                className="block w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/* ── Bell button (exported for Topbar) ───────────────────────────────────── */

interface NotificationsBellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unreadCount: number;
}

export function NotificationsBell({
  open,
  onOpenChange,
  unreadCount,
}: NotificationsBellProps) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="relative rounded-[8px] p-2 transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{ color: 'var(--vscode-text-tertiary)' }}
        title="Notifications"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {/* §23.A: emerald 6 px dot when unread > 0 — no number badge */}
        {unreadCount > 0 ? (
          <span
            className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full"
            style={{ background: 'var(--ggj-accent,#10B981)' }}
            aria-hidden
          />
        ) : null}
      </button>

      <NotificationsPanel open={open} onOpenChange={onOpenChange} />
    </div>
  );
}
